import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Checkbox } from "./components/ui/checkbox";
import { toast } from "sonner";
import { User, CheckCircle, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

const CandidatoOnboarding = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    telefono: '',
    email: '',
    password: '',
    confirmPassword: '',
      zona_preferenza_lavorativa: '',
    esperienza_flag: false,
    anni_esperienza: '',
    consenso_gdpr: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consenso_gdpr) {
      toast.error('È necessario accettare il trattamento dei dati personali');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }

    try {
      setSubmitting(true);

      // Create candidate with login credentials
      const candidateResponse = await fetch(`${API}/api/v1/auth/candidato/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          anni_esperienza: parseInt(formData.anni_esperienza) || 0,
          provenienza: 'Registrazione Diretta'
        })
      });

      if (!candidateResponse.ok) {
        const errorData = await candidateResponse.json();
        throw new Error(errorData.detail || 'Errore nella creazione del candidato');
      }

      const candidateData = await candidateResponse.json();

      toast.success('🎉 Registrazione completata! Benvenuto su InLeads');
      
      // Navigate to success page
      setTimeout(() => {
        navigate('/onboarding-success?type=candidato');
      }, 2000);

    } catch (error) {
      console.error('Error creating candidate:', error);
      toast.error(`❌ ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-emerald-50 py-8">
      <div className="container mx-auto px-4">
        <Card className="max-w-2xl mx-auto shadow-xl">
          <CardHeader className="text-center bg-gradient-to-r from-blue-600 to-emerald-600 text-white rounded-t-lg">
            <CardTitle className="flex items-center justify-center gap-2 text-2xl">
              <User className="w-6 h-6" />
              Candidatura InLeads
            </CardTitle>
            <CardDescription className="text-blue-100">
              Compila i tuoi dati per candidarti come agente immobiliare
            </CardDescription>
          </CardHeader>
          
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Dati personali */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">
                  📋 Dati Personali
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="nome">Nome *</Label>
                    <Input
                      id="nome"
                      value={formData.nome}
                      onChange={(e) => setFormData({...formData, nome: e.target.value})}
                      required
                      placeholder="Il tuo nome"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="cognome">Cognome *</Label>
                    <Input
                      id="cognome"
                      value={formData.cognome}
                      onChange={(e) => setFormData({...formData, cognome: e.target.value})}
                      required
                      placeholder="Il tuo cognome"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="telefono">Telefono *</Label>
                    <Input
                      id="telefono"
                      type="tel"
                      value={formData.telefono}
                      onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                      required
                      placeholder="+39 333 1234567"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      placeholder="tua@email.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="password">Password *</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      required
                      placeholder="Minimo 6 caratteri"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="confirmPassword">Conferma Password *</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                      required
                      placeholder="Ripeti la password"
                    />
                  </div>
                </div>

                  <div>
                    <Label htmlFor="zona_preferenza_lavorativa">Zona di Preferenza Lavorativa *</Label>
                    <Input
                      id="zona_preferenza_lavorativa"
                      value={formData.zona_preferenza_lavorativa}
                      onChange={(e) => setFormData({...formData, zona_preferenza_lavorativa: e.target.value})}
                      required
                      placeholder="Milano, Roma, Torino..."
                    />
                  </div>
              </div>

              {/* Esperienza */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">
                  💼 Esperienza Professionale
                </h3>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="esperienza_flag"
                    checked={formData.esperienza_flag}
                    onCheckedChange={(checked) => 
                      setFormData({...formData, esperienza_flag: checked})
                    }
                  />
                  <Label htmlFor="esperienza_flag">
                    Ho esperienza nel settore immobiliare
                  </Label>
                </div>

                {formData.esperienza_flag && (
                  <div>
                    <Label htmlFor="anni_esperienza">Anni di esperienza</Label>
                    <Select
                      value={formData.anni_esperienza}
                      onValueChange={(value) => setFormData({...formData, anni_esperienza: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleziona anni di esperienza" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 anno</SelectItem>
                        <SelectItem value="2">2 anni</SelectItem>
                        <SelectItem value="3">3 anni</SelectItem>
                        <SelectItem value="4">4 anni</SelectItem>
                        <SelectItem value="5">5 anni</SelectItem>
                        <SelectItem value="10">Più di 5 anni</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Sezione info gratuita */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">✅ Registrazione Completamente Gratuita</h4>
                <p className="text-sm text-blue-700">
                  Come candidato, avrai accesso gratuito a tutte le funzionalità della piattaforma. 
                  Nessun costo nascosto, nessun abbonamento richiesto.
                </p>
              </div>

              {/* GDPR Consent */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="consenso_gdpr"
                    checked={formData.consenso_gdpr}
                    onCheckedChange={(checked) => 
                      setFormData({...formData, consenso_gdpr: checked})
                    }
                    required
                  />
                  <Label htmlFor="consenso_gdpr" className="text-sm leading-relaxed">
                    Acconsento al trattamento dei miei dati personali secondo quanto previsto dal 
                    Regolamento UE 2016/679 (GDPR) per le finalità di selezione e recruiting. 
                    I dati saranno utilizzati esclusivamente per valutare la candidatura e contattare 
                    il candidato. *
                  </Label>
                </div>
              </div>

              {/* Submit button */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="flex-1"
                  disabled={submitting}
                >
                  Annulla
                </Button>
                <Button
                  type="submit"
                  disabled={submitting || !formData.consenso_gdpr}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Invio in corso...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Crea Account Candidato
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CandidatoOnboarding;